import { useMemo } from "react"

import { useTranslation } from "react-i18next"
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"

import type { FeedbackStatsResponse } from "@/api/feedback-api"

import { AlertOctagon, Bug, Loader2, ShieldAlert, ThumbsUp } from "@/components/icons"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"

import { useAdminFeedbackStatsQuery } from "../../hooks/admin-feedback-queries"

const CHART_HEIGHT = 260

/**
 * Analytics panels at the top of the admin Feedback section — five views
 * answering "what's going wrong right now":
 *
 *   1. Volume over time         (stacked area: 👍 / 👎 / bugs / incidents)
 *   2. Top reported pages       (horizontal bar)
 *   3. Bug severity mix         (donut)
 *   4. Escalation rate          (% KPI + sparkline)
 *   5. Repeat offenders         (list of messages/sessions with 3+ 👎)
 */
export function FeedbackAnalytics() {
  const { t } = useTranslation()
  const { data, isLoading, error } = useAdminFeedbackStatsQuery(30)

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="text-muted-foreground size-6 animate-spin" />
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="text-muted-foreground py-8 text-center text-sm">
        {t("adminDashboard.feedback.analytics.loadError", "Could not load analytics.")}
      </div>
    )
  }

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <VolumePanel data={data} />
      <TopPagesPanel data={data} />
      <SeverityPanel data={data} />
      <EscalationPanel data={data} />
    </div>
  )
}

// -----------------------------------------------------------------------------
// Panels
// -----------------------------------------------------------------------------

function VolumePanel({ data }: { data: FeedbackStatsResponse }) {
  const { t } = useTranslation()
  const empty = data.volume_series.every((p) => p.up + p.down + p.bug + p.incident === 0)
  return (
    <Card className="rounded-lg shadow-sm">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <ThumbsUp className="size-4" />
          {t("adminDashboard.feedback.analytics.volume.title", "Feedback over time")}
        </CardTitle>
        <CardDescription>
          {t(
            "adminDashboard.feedback.analytics.volume.description",
            "Daily submissions across the three lanes for the last 30 days."
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {empty ? (
          <EmptyBlock>
            {t(
              "adminDashboard.feedback.analytics.volume.empty",
              "No feedback in the last 30 days."
            )}
          </EmptyBlock>
        ) : (
          <ResponsiveContainer height={CHART_HEIGHT} width="100%">
            <AreaChart data={data.volume_series}>
              <defs>
                <linearGradient id="fb-up" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="5%" stopColor="hsl(var(--success))" stopOpacity={0.7} />
                  <stop offset="95%" stopColor="hsl(var(--success))" stopOpacity={0.05} />
                </linearGradient>
                <linearGradient id="fb-down" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="5%" stopColor="hsl(var(--destructive))" stopOpacity={0.7} />
                  <stop offset="95%" stopColor="hsl(var(--destructive))" stopOpacity={0.05} />
                </linearGradient>
                <linearGradient id="fb-bug" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="5%" stopColor="hsl(var(--warning))" stopOpacity={0.7} />
                  <stop offset="95%" stopColor="hsl(var(--warning))" stopOpacity={0.05} />
                </linearGradient>
                <linearGradient id="fb-incident" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="5%" stopColor="hsl(var(--chart-1))" stopOpacity={0.7} />
                  <stop offset="95%" stopColor="hsl(var(--chart-1))" stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <XAxis dataKey="date" fontSize={11} tickFormatter={shortDate} />
              <YAxis allowDecimals={false} fontSize={11} />
              <Tooltip />
              <Legend />
              <Area
                dataKey="up"
                fill="url(#fb-up)"
                name={t("adminDashboard.feedback.analytics.volume.up", "Helpful")}
                stackId="a"
                stroke="hsl(var(--success-ink))"
                type="monotone"
              />
              <Area
                dataKey="down"
                fill="url(#fb-down)"
                name={t("adminDashboard.feedback.analytics.volume.down", "Not helpful")}
                stackId="a"
                stroke="hsl(var(--destructive-ink))"
                type="monotone"
              />
              <Area
                dataKey="bug"
                fill="url(#fb-bug)"
                name={t("adminDashboard.feedback.analytics.volume.bug", "Bugs")}
                stackId="a"
                stroke="hsl(var(--warning-ink))"
                type="monotone"
              />
              <Area
                dataKey="incident"
                fill="url(#fb-incident)"
                name={t("adminDashboard.feedback.analytics.volume.incident", "Incidents")}
                stackId="a"
                stroke="hsl(var(--primary-ink))"
                type="monotone"
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  )
}

function TopPagesPanel({ data }: { data: FeedbackStatsResponse }) {
  const { t } = useTranslation()
  return (
    <Card className="rounded-lg shadow-sm">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Bug className="size-4" />
          {t("adminDashboard.feedback.analytics.pages.title", "Most reported pages")}
        </CardTitle>
        <CardDescription>
          {t(
            "adminDashboard.feedback.analytics.pages.description",
            "Pages and features users flag most (bugs + incidents)."
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {data.top_pages.length === 0 ? (
          <EmptyBlock>
            {t(
              "adminDashboard.feedback.analytics.pages.empty",
              "No bug or incident reports in this window."
            )}
          </EmptyBlock>
        ) : (
          <ResponsiveContainer height={CHART_HEIGHT} width="100%">
            <BarChart data={data.top_pages} layout="vertical" margin={{ left: 40 }}>
              <XAxis allowDecimals={false} fontSize={11} type="number" />
              <YAxis dataKey="page" fontSize={11} type="category" width={120} />
              <Tooltip />
              <Bar
                dataKey="count"
                fill="hsl(var(--warning))"
                radius={[0, 4, 4, 0]}
                stroke="hsl(var(--foreground))"
                strokeWidth={1}
              />
            </BarChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  )
}

const SEVERITY_COLORS: Record<string, string> = {
  cosmetic: "hsl(var(--muted-foreground))",
  annoying: "hsl(var(--warning))",
  blocks_workflow: "hsl(var(--destructive) / 0.6)",
  work_stopped: "hsl(var(--destructive))",
}

function SeverityPanel({ data }: { data: FeedbackStatsResponse }) {
  const { t } = useTranslation()
  const hasAny = data.severity_mix.some((row) => row.count > 0)
  const translated = useMemo(
    () =>
      data.severity_mix.map((row) => ({
        ...row,
        label: t(`feedback.bug.severity.${row.severity}`, defaultSeverityLabel(row.severity)),
      })),
    [data.severity_mix, t]
  )
  return (
    <Card className="rounded-lg shadow-sm">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <AlertOctagon className="size-4" />
          {t("adminDashboard.feedback.analytics.severity.title", "Bug severity mix")}
        </CardTitle>
        <CardDescription>
          {t(
            "adminDashboard.feedback.analytics.severity.description",
            "How serious are the bugs being reported?"
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {!hasAny ? (
          <EmptyBlock>
            {t("adminDashboard.feedback.analytics.severity.empty", "No bug reports yet.")}
          </EmptyBlock>
        ) : (
          <ResponsiveContainer height={CHART_HEIGHT} width="100%">
            <PieChart>
              <Pie
                data={translated}
                dataKey="count"
                innerRadius={60}
                nameKey="label"
                outerRadius={100}
                paddingAngle={2}
              >
                {translated.map((row) => (
                  <Cell
                    key={row.severity}
                    fill={SEVERITY_COLORS[row.severity] ?? "hsl(var(--muted))"}
                    stroke="hsl(var(--foreground))"
                    strokeWidth={1}
                  />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  )
}

function EscalationPanel({ data }: { data: FeedbackStatsResponse }) {
  const { t } = useTranslation()
  const pct = Math.round(data.escalation.rate * 100)
  const hasBugs = data.escalation.total_bugs > 0
  return (
    <Card className="rounded-lg shadow-sm">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <ShieldAlert className="size-4" />
          {t("adminDashboard.feedback.analytics.escalation.title", "Escalation rate")}
        </CardTitle>
        <CardDescription>
          {t(
            "adminDashboard.feedback.analytics.escalation.description",
            "Bugs that crossed the threshold and reached support."
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {!hasBugs ? (
          <EmptyBlock>
            {t("adminDashboard.feedback.analytics.escalation.empty", "No bug reports yet.")}
          </EmptyBlock>
        ) : (
          <>
            <div className="mb-3 flex items-baseline gap-3">
              <span className="text-3xl font-bold tabular-nums">{pct}%</span>
              <span className="text-muted-foreground text-xs">
                {t("adminDashboard.feedback.analytics.escalation.ratio", {
                  defaultValue: "{{escalated}} of {{total}} bugs escalated",
                  escalated: data.escalation.escalated_bugs,
                  total: data.escalation.total_bugs,
                })}
              </span>
            </div>
            <ResponsiveContainer height={CHART_HEIGHT - 60} width="100%">
              <AreaChart data={data.escalation.series}>
                <XAxis dataKey="date" fontSize={11} tickFormatter={shortDate} />
                <YAxis
                  domain={[0, 1]}
                  fontSize={11}
                  tickFormatter={(v: number) => `${Math.round(v * 100)}%`}
                  width={40}
                />
                <Tooltip
                  formatter={(v) => (typeof v === "number" ? `${Math.round(v * 100)}%` : String(v))}
                />
                <Area
                  dataKey="rate"
                  fill="hsl(var(--warning))"
                  fillOpacity={0.2}
                  name="rate"
                  stroke="hsl(var(--warning-ink))"
                  type="monotone"
                />
              </AreaChart>
            </ResponsiveContainer>
          </>
        )}
      </CardContent>
    </Card>
  )
}

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

function EmptyBlock({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-muted-foreground flex h-40 items-center justify-center text-sm">
      {children}
    </div>
  )
}

function shortDate(date: string): string {
  // yyyy-mm-dd → mm-dd for axis labels.
  return date.length >= 10 ? date.slice(5) : date
}

function defaultSeverityLabel(severity: string): string {
  return severity
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ")
}
